import { motion } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguageStore } from '../store/languageStore';
import { useEffect } from 'react';
import { LanguageToggle } from './LanguageToggle';
import BoltBadge from './BoltBadge';

export const Hero = () => {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguageStore();

  useEffect(() => {
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      const el = document.getElementById(scrollTarget);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      sessionStorage.removeItem('scrollTarget');
    }
  }, []);

  const handleGetStarted = () => {
    navigate('/start-here');
  };

  const quotes = {
    EN: "Asking for help is a strength — not a weakness.",
    ES: "Pedir ayuda es una fortaleza — no una debilidad.",
    ZH: "寻求帮助是一种力量 — 而不是软弱。",
    VI: "Xin giúp đỡ là một sức mạnh — không phải là điểm yếu.",
    FIL: "Ang paghingi ng tulong ay kalakasan — hindi kahinaan.",
    KO: "도움을 요청하는 것은 강점입니다 — 약점이 아닙니다.",
    AR: "طلب المساعدة قوة — وليس ضعفاً.",
  };

  const translations = {
    EN: {
      title: "RightNowAid",
      subtitle: "Emergency Assistance at Your Fingertips",
      description: "Get instant guidance and support during emergencies. Our AI-powered assistant provides real-time instructions, connects you with emergency services, and helps you stay calm and focused.",
      getStarted: "Get Started",
      problemsTitle: "What Problems Does RightNowAid Solve?",
      problemsSubtitle: "We remove the barriers that make getting emergency help stressful, slow, or confusing.",
      noMoreLongWaits: "No More Long Waits",
      noMoreLongWaitsDesc: "Get instant answers and support—no more waiting on hold or for callbacks.",
      noConfusingPaperwork: "No Confusing Paperwork",
      noConfusingPaperworkDesc: "We guide you step-by-step, so you know exactly what to do and what to bring.",
      noLanguageBarriers: "No Language Barriers",
      noLanguageBarriersDesc: "Get help in your language—our AI supports English, Spanish, Chinese, Vietnamese, Filipino, Korean, and Arabic.",
      noMoreDeadEnds: "No More Dead Ends",
      noMoreDeadEndsDesc: "We connect you directly to real, available programs—no more searching for hours or hitting roadblocks.",
      stats: {
        activeUsers: "Active Users",
        emergencyResponses: "Emergency Responses",
        successRate: "Success Rate",
        responseTime: "Response Time"
      }
    },
    ES: {
      title: "RightNowAid",
      subtitle: "Asistencia de Emergencia al Alcance de tu Mano",
      description: "Obtén orientación y apoyo instantáneo durante emergencias. Nuestro asistente impulsado por IA proporciona instrucciones en tiempo real, te conecta con servicios de emergencia y te ayuda a mantener la calma y el enfoque.",
      getStarted: "Comenzar",
      problemsTitle: "¿Qué Problemas Resuelve RightNowAid?",
      problemsSubtitle: "Eliminamos las barreras que hacen que obtener ayuda de emergencia sea estresante, lento o confuso.",
      noMoreLongWaits: "No Más Esperas Largas",
      noMoreLongWaitsDesc: "Obtén respuestas y apoyo instantáneo—sin más esperas en línea o devoluciones de llamadas.",
      noConfusingPaperwork: "No Más Trámites Confusos",
      noConfusingPaperworkDesc: "Te guiamos paso a paso, para que sepas exactamente qué hacer y qué traer.",
      noLanguageBarriers: "Sin Barreras de Idioma",
      noLanguageBarriersDesc: "Obtén ayuda en tu idioma—nuestra IA soporta inglés, español, chino, vietnamita, filipino, coreano y árabe.",
      noMoreDeadEnds: "No Más Callejones Sin Salida",
      noMoreDeadEndsDesc: "Te conectamos directamente con programas reales y disponibles—sin más búsquedas de horas o bloqueos.",
      stats: {
        activeUsers: "Usuarios Activos",
        emergencyResponses: "Respuestas de Emergencia",
        successRate: "Tasa de Éxito",
        responseTime: "Tiempo de Respuesta"
      }
    },
    ZH: {
      title: "RightNowAid",
      subtitle: "紧急援助触手可及",
      description: "在紧急情况下获得即时指导和支持。我们的人工智能助手提供实时指导，连接紧急服务，帮助您保持冷静和专注。",
      getStarted: "开始使用",
      problemsTitle: "RightNowAid 解决什么问题？",
      problemsSubtitle: "我们消除那些使获得紧急帮助变得压力大、缓慢或混乱的障碍。",
      noMoreLongWaits: "不再长时间等待",
      noMoreLongWaitsDesc: "获得即时答案和支持—不再等待电话或回电。",
      noConfusingPaperwork: "不再有复杂的文书工作",
      noConfusingPaperworkDesc: "我们一步步指导您，让您确切知道该做什么和带什么。",
      noLanguageBarriers: "没有语言障碍",
      noLanguageBarriersDesc: "用您的语言获得帮助—我们的AI支持英语、西班牙语、中文、越南语、菲律宾语、韩语和阿拉伯语。",
      noMoreDeadEnds: "不再有死胡同",
      noMoreDeadEndsDesc: "我们直接将您连接到真实可用的项目—不再需要搜索数小时或遇到障碍。",
      stats: {
        activeUsers: "活跃用户",
        emergencyResponses: "紧急响应",
        successRate: "成功率",
        responseTime: "响应时间"
      }
    },
    VI: {
      title: "RightNowAid",
      subtitle: "Hỗ Trợ Khẩn Cấp Ngay Trong Tầm Tay",
      description: "Nhận hướng dẫn và hỗ trợ ngay lập tức trong trường hợp khẩn cấp. Trợ lý AI của chúng tôi cung cấp hướng dẫn thời gian thực, kết nối bạn với dịch vụ khẩn cấp và giúp bạn giữ bình tĩnh và tập trung.",
      getStarted: "Bắt Đầu",
      problemsTitle: "RightNowAid Giải Quyết Những Vấn Đề Gì?",
      problemsSubtitle: "Chúng tôi loại bỏ những rào cản khiến việc nhận trợ giúp khẩn cấp trở nên căng thẳng, chậm chạp hoặc khó hiểu.",
      noMoreLongWaits: "Không Còn Chờ Đợi Lâu",
      noMoreLongWaitsDesc: "Nhận câu trả lời và hỗ trợ ngay lập tức—không còn chờ đợi trên điện thoại hoặc chờ gọi lại.",
      noConfusingPaperwork: "Không Còn Giấy Tờ Phức Tạp",
      noConfusingPaperworkDesc: "Chúng tôi hướng dẫn bạn từng bước, để bạn biết chính xác phải làm gì và mang theo những gì.",
      noLanguageBarriers: "Không Còn Rào Cản Ngôn Ngữ",
      noLanguageBarriersDesc: "Nhận trợ giúp bằng ngôn ngữ của bạn—AI của chúng tôi hỗ trợ tiếng Anh, Tây Ban Nha, Trung Quốc, Việt Nam, Philippines, Hàn Quốc và Ả Rập.",
      noMoreDeadEnds: "Không Còn Ngõ Cụt",
      noMoreDeadEndsDesc: "Chúng tôi kết nối bạn trực tiếp với các chương trình thực tế và có sẵn—không còn tìm kiếm hàng giờ hoặc gặp trở ngại.",
      stats: {
        activeUsers: "Người Dùng Hoạt Động",
        emergencyResponses: "Phản Hồi Khẩn Cấp",
        successRate: "Tỷ Lệ Thành Công",
        responseTime: "Thời Gian Phản Hồi"
      }
    },
    FIL: {
      title: "RightNowAid",
      subtitle: "Emergency Assistance sa Iyong Mga Kamay",
      description: "Kumuha ng instant na gabay at suporta sa mga emergency. Ang aming AI-powered na assistant ay nagbibigay ng real-time na mga tagubilin, kumokonekta sa iyo sa mga emergency service, at tumutulong sa iyong manatiling kalmado at nakatuon.",
      getStarted: "Magsimula",
      problemsTitle: "Anong mga Problema ang Nilulutas ng RightNowAid?",
      problemsSubtitle: "Tinatanggal namin ang mga hadlang na nagpapahirap, nagpapabagal, o nagpapalito sa pagkuha ng emergency na tulong.",
      noMoreLongWaits: "Wala Nang Mahabang Paghihintay",
      noMoreLongWaitsDesc: "Kumuha ng instant na mga sagot at suporta—wala nang paghihintay sa telepono o paghihintay ng tawag pabalik.",
      noConfusingPaperwork: "Wala Nang Nakakalitong Papel",
      noConfusingPaperworkDesc: "Ginagabayan ka namin nang hakbang-hakbang, para alam mo nang eksakto kung ano ang gagawin at kung ano ang dadalhin.",
      noLanguageBarriers: "Wala Nang Hadlang sa Wika",
      noLanguageBarriersDesc: "Kumuha ng tulong sa iyong wika—ang aming AI ay sumusuporta sa Ingles, Espanyol, Intsik, Vietnamese, Filipino, Korean at Arabic.",
      noMoreDeadEnds: "Wala Nang Dead Ends",
      noMoreDeadEndsDesc: "Ikokonekta ka namin nang direkta sa mga tunay at available na programa—wala nang paghahanap ng oras o pagharap sa mga hadlang.",
      stats: {
        activeUsers: "Mga Aktibong User",
        emergencyResponses: "Mga Emergency Response",
        successRate: "Rate ng Tagumpay",
        responseTime: "Oras ng Response"
      }
    },
    KO: {
      title: "RightNowAid",
      subtitle: "긴급 지원이 손끝에",
      description: "비상 상황에서 즉각적인 안내와 지원을 받으세요. 우리의 AI 기반 어시스턴트는 실시간 지침을 제공하고, 비상 서비스와 연결하며, 침착하고 집중할 수 있도록 도와드립니다.",
      getStarted: "시작하기",
      problemsTitle: "RightNowAid가 해결하는 문제는 무엇인가요?",
      problemsSubtitle: "비상 지원을 받는 것을 스트레스 받고, 느리고, 혼란스럽게 만드는 장벽을 제거합니다.",
      noMoreLongWaits: "더 이상 오래 기다리지 않음",
      noMoreLongWaitsDesc: "즉각적인 답변과 지원을 받으세요—더 이상 전화 대기나 콜백을 기다리지 않습니다.",
      noConfusingPaperwork: "더 이상 혼란스러운 서류 작업 없음",
      noConfusingPaperworkDesc: "정확히 무엇을 해야 하고 무엇을 가져와야 하는지 알 수 있도록 단계별로 안내해 드립니다.",
      noLanguageBarriers: "더 이상 언어 장벽 없음",
      noLanguageBarriersDesc: "귀하의 언어로 도움을 받으세요—우리의 AI는 영어, 스페인어, 중국어, 베트남어, 필리핀어, 한국어 및 아랍어를 지원합니다.",
      noMoreDeadEnds: "더 이상 막다른 골목 없음",
      noMoreDeadEndsDesc: "실제로 사용 가능한 프로그램에 직접 연결해 드립니다—더 이상 몇 시간 동안 검색하거나 장애물에 부딪히지 않습니다.",
      stats: {
        activeUsers: "활성 사용자",
        emergencyResponses: "비상 대응",
        successRate: "성공률",
        responseTime: "응답 시간"
      }
    },
    AR: {
      title: "RightNowAid",
      subtitle: "المساعدة الطارئة في متناول يدك",
      description: "احصل على التوجيه والدعم الفوري أثناء حالات الطوارئ. يساعدك مساعدنا المدعوم بالذكاء الاصطناعي بتقديم تعليمات في الوقت الفعلي، وربطك بخدمات الطوارئ، ومساعدتك على البقاء هادئًا ومركّزًا.",
      getStarted: "ابدأ الآن",
      problemsTitle: "ما هي المشاكل التي يحلها RightNowAid؟",
      problemsSubtitle: "نحن نزيل الحواجز التي تجعل الحصول على المساعدة الطارئة مرهقًا أو بطيئًا أو مربكًا.",
      noMoreLongWaits: "لا مزيد من الانتظار الطويل",
      noMoreLongWaitsDesc: "احصل على إجابات ودعم فوري—لا مزيد من الانتظار على الهاتف أو انتظار الرد.",
      noConfusingPaperwork: "لا مزيد من الأوراق المربكة",
      noConfusingPaperworkDesc: "نحن نرشدك خطوة بخطوة، حتى تعرف بالضبط ما يجب القيام به وما يجب إحضاره.",
      noLanguageBarriers: "لا مزيد من حواجز اللغة",
      noLanguageBarriersDesc: "احصل على المساعدة بلغتك—يدعم الذكاء الاصطناعي لدينا الإنجليزية والإسبانية والصينية والفيتنامية والفلبينية والكورية والعربية.",
      noMoreDeadEnds: "لا مزيد من النهايات المسدودة",
      noMoreDeadEndsDesc: "نحن نربطك مباشرة ببرامج حقيقية ومتاحة—لا مزيد من البحث لساعات أو مواجهة العقبات.",
      stats: {
        activeUsers: "المستخدمون النشطون",
        emergencyResponses: "الاستجابات الطارئة",
        successRate: "معدل النجاح",
        responseTime: "وقت الاستجابة"
      }
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-b from-background to-background/80">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />

      {/* Content */}
      <div className="container relative mx-auto flex min-h-screen flex-col items-center justify-center px-4 pt-32 sm:pt-32 pb-16 sm:pb-32 text-center">
        {/* Language Toggle - Lower down to avoid header overlap */}
        <div className="absolute top-64 sm:top-48 right-4 z-10">
          <LanguageToggle />
        </div>

        {/* "Powered by Bolt" ribbon - Moved up with more margin */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center gap-3 sm:gap-4 mt-24 mb-16 sm:mb-24"
        >
          <BoltBadge />
        </motion.div>

        {/* Heart Icon */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="flex justify-center mb-4 sm:mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            fill="none"
            stroke="#0891b2"
            strokeWidth="3"
            className="w-24 h-24"
          >
            <path
              d="M24 40s-10.5-8.5-13.5-14C7.5 20 10.5 13 18 13c3.5 0 6 2.5 6 2.5S26.5 13 30 13c7.5 0 10.5 7 7.5 13-3 5.5-13.5 14-13.5 14z"
              stroke="#0891b2"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-4 sm:mb-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground"
        >
          <span className="gradient-text">{translations[selectedLanguage].title}</span>
          <br />
          <span className="text-foreground">{translations[selectedLanguage].subtitle}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-6 sm:mb-8 max-w-2xl text-base sm:text-lg text-muted-foreground"
        >
          {translations[selectedLanguage].description}
        </motion.p>

        {/* Motivational Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-6 sm:mb-8 max-w-2xl mx-auto"
        >
          <div className="flex flex-col items-center gap-2 sm:gap-3">
            <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            <p className="text-lg sm:text-xl md:text-2xl font-medium text-foreground italic leading-relaxed">
              {quotes[selectedLanguage]}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex justify-center"
        >
          <button
            onClick={handleGetStarted}
            className="btn btn-primary group inline-flex items-center justify-center gap-2 rounded-lg px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-medium"
          >
            {translations[selectedLanguage].getStarted}
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>

        {/* Pain Points Resolved Section */}
        <section className="relative py-12 sm:py-16 w-full">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-2xl text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground"
              >
                {translations[selectedLanguage].problemsTitle}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-3 sm:mt-4 text-base sm:text-lg text-muted-foreground"
              >
                {translations[selectedLanguage].problemsSubtitle}
              </motion.p>
            </div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }}
              className="mt-10 sm:mt-14 grid gap-4 sm:gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            >
              {/* Pain Point 1 */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="group relative rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-red-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">{translations[selectedLanguage].noMoreLongWaits}</h3>
                <p className="text-sm text-muted-foreground">{translations[selectedLanguage].noMoreLongWaitsDesc}</p>
                <div className="absolute inset-0 rounded-xl border-2 border-primary/0 transition-colors group-hover:border-primary/20 pointer-events-none" />
              </motion.div>
              {/* Pain Point 2 */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="group relative rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-red-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8M8 16h8M8 8h8" /></svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">{translations[selectedLanguage].noConfusingPaperwork}</h3>
                <p className="text-sm text-muted-foreground">{translations[selectedLanguage].noConfusingPaperworkDesc}</p>
                <div className="absolute inset-0 rounded-xl border-2 border-primary/0 transition-colors group-hover:border-primary/20 pointer-events-none" />
              </motion.div>
              {/* Pain Point 3 */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="group relative rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-red-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">{translations[selectedLanguage].noLanguageBarriers}</h3>
                <p className="text-sm text-muted-foreground">{translations[selectedLanguage].noLanguageBarriersDesc}</p>
                <div className="absolute inset-0 rounded-xl border-2 border-primary/0 transition-colors group-hover:border-primary/20 pointer-events-none" />
              </motion.div>
              {/* Pain Point 4 */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="group relative rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-red-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">{translations[selectedLanguage].noMoreDeadEnds}</h3>
                <p className="text-sm text-muted-foreground">{translations[selectedLanguage].noMoreDeadEndsDesc}</p>
                <div className="absolute inset-0 rounded-xl border-2 border-primary/0 transition-colors group-hover:border-primary/20 pointer-events-none" />
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-16 grid grid-cols-2 gap-8 sm:grid-cols-4"
        >
          {[
            { label: translations[selectedLanguage].stats.activeUsers, value: '10K+' },
            { label: translations[selectedLanguage].stats.emergencyResponses, value: '50K+' },
            { label: translations[selectedLanguage].stats.successRate, value: '99.9%' },
            { label: translations[selectedLanguage].stats.responseTime, value: '< 2s' },
          ].map((stat, _index) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl font-bold text-foreground">{stat.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
};